import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "reactstrap";
import { Catalogue } from "../../types/catalogue.types";
import notifications from "../../common/Components/Notifications/Notification";
import { CATALOGUE_LIST_PATH, parseCatalogueFollowUpQuery } from "./cataloguePaths";
import { hasNamedCatalogueSeed, resolveCatalogue } from "./catalogueResolve";

// Import components
import CatalogueList from "./CatalogueList";
import CatalogueDetail from "./CatalogueDetail";

// Import actions
import { getCatAlogue } from "../../Store/catelogue/actions";
import "./catalogue-manager.scss";

interface CatalogueOrgManagerProps {
  getCatAlogue: (payload: any, callback: (res: any) => void) => void;
}

const escapeCsvCell = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = (filename: string, header: string[], rows: string[][]) => {
  const lines = [header.map(escapeCsvCell).join(","), ...rows.map((r) => r.map(escapeCsvCell).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

type CatalogueResumeState = {
  openCatalogueId?: string;
  catalogue?: Catalogue;
  openTab?: "basic" | "products" | "pricing" | "performance" | "inquiries" | "settings" | "activity";
};

const followUpOpenId = (
  incoming?: CatalogueResumeState,
  search?: string
): string =>
  incoming?.openCatalogueId ||
  incoming?.catalogue?.id ||
  parseCatalogueFollowUpQuery(search || "")?.catalogueId ||
  "";

const CatalogueOrgManager: React.FC<CatalogueOrgManagerProps> = ({ getCatAlogue }) => {
  const history = useHistory();
  const location = useLocation<CatalogueResumeState>();
  const incoming = location.state;
  const followUp = parseCatalogueFollowUpQuery(location.search);
  const openId = followUpOpenId(incoming, location.search);
  const seededIncoming = incoming?.catalogue;
  const [activeCatalogue, setActiveCatalogue] = useState<Catalogue | null>(() =>
    hasNamedCatalogueSeed(seededIncoming, openId) ? seededIncoming : null
  );
  const [resumeTab, setResumeTab] = useState(incoming?.openTab || followUp?.tab);
  const [resumeInquiryId, setResumeInquiryId] = useState(followUp?.inquiryId);
  const [resumeSubmissionId, setResumeSubmissionId] = useState(followUp?.submissionId);
  const [resumeLinkToken, setResumeLinkToken] = useState(followUp?.linkToken);
  const [resolving, setResolving] = useState(
    () => Boolean(openId) && !hasNamedCatalogueSeed(seededIncoming, openId)
  );
  const [resolveError, setResolveError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const query = parseCatalogueFollowUpQuery(location.search);
    const incomingState = location.state;
    const nextOpenId = followUpOpenId(incomingState, location.search);
    if (!nextOpenId) {
      return;
    }

    const seeded = incomingState?.catalogue;
    const nextTab = incomingState?.openTab || query?.tab;
    const nextInquiryId = query?.inquiryId;
    const nextSubmissionId = query?.submissionId;
    const nextLinkToken = query?.linkToken;
    let cancelled = false;

    const applyOpenedCatalogue = (catalogue: Catalogue) => {
      if (cancelled) {
        return;
      }
      setActiveCatalogue(catalogue);
      setResumeTab(nextTab);
      setResumeInquiryId(nextInquiryId);
      setResumeSubmissionId(nextSubmissionId);
      setResumeLinkToken(nextLinkToken);
      setResolveError("");
      setResolving(false);
      history.replace({ pathname: CATALOGUE_LIST_PATH });
    };

    if (hasNamedCatalogueSeed(seeded, nextOpenId)) {
      applyOpenedCatalogue(seeded);
      return () => {
        cancelled = true;
      };
    }

    setResolving(true);
    setResolveError("");
    void resolveCatalogue(nextOpenId, seeded).then((result) => {
      if (cancelled) {
        return;
      }
      if (result.error) {
        setResolving(false);
        setActiveCatalogue(null);
        setResolveError(result.error);
        history.replace({ pathname: CATALOGUE_LIST_PATH });
        return;
      }
      if (result.catalogue) {
        applyOpenedCatalogue(result.catalogue);
        return;
      }
      setResolving(false);
      history.replace({ pathname: CATALOGUE_LIST_PATH });
    });

    return () => {
      cancelled = true;
    };
  }, [history, location.search, location.state]);

  const handleOpenCatalogue = (catalogue: Catalogue) => {
    setResumeTab(undefined);
    setResumeInquiryId(undefined);
    setResumeSubmissionId(undefined);
    setResumeLinkToken(undefined);
    setResolveError("");
    setResolving(false);
    setActiveCatalogue(catalogue);
  };

  const handleBackToCatalogueList = () => {
    setResumeTab(undefined);
    setResumeInquiryId(undefined);
    setResumeSubmissionId(undefined);
    setResumeLinkToken(undefined);
    setResolveError("");
    setResolving(false);
    setActiveCatalogue(null);
  };

  const exportSingleCatalogue = (c: Catalogue) => {
    const header = [
      "id",
      "name",
      "description",
      "category",
      "targetMarket",
      "status",
      "productsCountUi",
      "viewCountUi",
      "created",
    ];
    const row = [
      c.id,
      c.name,
      c.description || "",
      c.category || "",
      c.targetMarket || "",
      c.status || "",
      String(c.products ?? ""),
      String(c.accessed ?? ""),
      c.created || "",
    ];
    downloadCsv(`hkp-catalogue-${c.id}.csv`, header, [row]);
    notifications({
      title: "Export ready",
      message: "Current catalogue row downloaded as CSV.",
      type: "success",
    });
  };

  const handleExportData = () => {
    if (activeCatalogue) {
      exportSingleCatalogue(activeCatalogue);
      return;
    }

    setExporting(true);
    const pageSize = 100;
    const collected: any[] = [];

    const fetchPage = (offset: number) => {
      getCatAlogue(
        {
          offset,
          limit: pageSize,
          sortBy: "createdAt",
          sortOrder: "desc",
        },
        (res: any) => {
          if (!res?.success || !Array.isArray(res?.data?.docs)) {
            setExporting(false);
            notifications({
              title: "Export failed",
              message: res?.message || "Could not load catalogues for export.",
              type: "danger",
            });
            return;
          }
          collected.push(...res.data.docs);
          const hasMore = res.data.pagination?.hasMore === true;
          if (hasMore) {
            fetchPage(offset + pageSize);
            return;
          }

          const header = [
            "id",
            "name",
            "description",
            "category",
            "targetMarket",
            "viewCount",
            "active",
            "createdAt",
            "updatedAt",
          ];
          const rows = collected.map((doc: any) => [
            doc._id ?? doc.id ?? "",
            doc.name ?? "",
            doc.description ?? "",
            doc.category ?? "",
            doc.targetMarket ?? "",
            doc.viewCount != null ? String(doc.viewCount) : "",
            doc.active != null ? String(doc.active) : doc.isActive != null ? String(doc.isActive) : "",
            doc.createdAt ?? "",
            doc.updatedAt ?? "",
          ]);
          const stamp = new Date().toISOString().slice(0, 10);
          downloadCsv(`hkp-catalogues-export-${stamp}.csv`, header, rows);
          setExporting(false);
          notifications({
            title: "Export ready",
            message: `Downloaded ${rows.length} catalogue row(s).`,
            type: "success",
          });
        }
      );
    };

    fetchPage(0);
  };

  return (
    <div className="catalogue-org-manager">
      <div className="container-fluid px-3 px-sm-4 px-lg-5 py-3">
        <div className="row">
          <div className="col-12">
            {resolving ? (
              <p className="catalogue-add-from-inventory-lead">Loading catalogue…</p>
            ) : null}
            {resolveError && !resolving ? (
              <div className="catalogue-add-from-inventory-error">
                <p className="catalogue-add-from-inventory-lead">{resolveError}</p>
                <Button
                  className="catalogue-toolbar-btn catalogue-toolbar-btn-outline"
                  type="button"
                  onClick={() => setResolveError("")}
                >
                  Back to catalogues
                </Button>
              </div>
            ) : null}
            {!resolving && !resolveError && !activeCatalogue && (
              <CatalogueList
                onOpenCatalogue={handleOpenCatalogue}
                onExportData={handleExportData}
                exporting={exporting}
              />
            )}
            {!resolving && !resolveError && activeCatalogue && (
              <CatalogueDetail
                catalogue={activeCatalogue}
                initialTab={resumeTab}
                initialInquiryId={resumeInquiryId}
                initialSubmissionId={resumeSubmissionId}
                initialLinkToken={resumeLinkToken}
                onBack={handleBackToCatalogueList}
                onExportData={handleExportData}
                exporting={exporting}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(null, {
  getCatAlogue
})(CatalogueOrgManager);
